import React, { memo, useEffect, useMemo, useState } from "react";
import { Sidebar } from "@/components/ui/MultiSidebar";
import { Footer } from "./Footer";
import { useFilters } from "./hooks/useFilters";
import { useForm, Control, FormProvider, useWatch } from "react-hook-form";
import { FilterInputData } from "./types";
import { SearchableFilters } from "./SearchableFilters";
import { useReactiveVar } from "@apollo/client";
import {
  filterStateVar,
  initialFilterStateVar,
} from "@/reactiveVars/organization";
import { FilterState } from "./FilterState";
import { filterQueriesAreLoading } from "@/reactiveVars/global";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useFilterEnabled } from "@/components/FilterEnabledProvider";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { buildOptionLookup, buildFilterAnalyticsProperties } from "./analytics";

export function FilterSidebar() {
  const { filters, loading } = useFilters();
  const initialFilterState = useReactiveVar(initialFilterStateVar);
  const methods = useForm<FilterInputData>();
  const { handleSubmit, control, reset } = methods;
  const { trackEvent } = useAnalytics();
  const { filterAnalyticsEvent } = useFilterEnabled();
  const { hasOrgFeature } = useOrgPermissions();

  const optionLookup = useMemo(() => buildOptionLookup(filters), [filters]);
  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  const onSubmit = (data: FilterInputData) => {
    const filterState = new FilterState(data);
    filterStateVar(filterState);

    if (filterAnalyticsEvent) {
      trackEvent(
        filterAnalyticsEvent,
        buildFilterAnalyticsProperties(data, optionLookup, hasRise),
      );
    }
  };

  const onClear = () => {
    filterStateVar(new FilterState({}));
  };

  // Reset form if initialFilterState is updated
  useEffect(() => {
    const parsed = FilterInputData.parse(initialFilterState.data);
    reset(parsed);
  }, [initialFilterState, reset]);

  return (
    <Sidebar
      side="right"
      sidebarId="filter"
      style={
        {
          "--sidebar": "var(--background)",
        } as React.CSSProperties
      }
    >
      <FormProvider {...methods}>
        <SearchableFilters loading={loading} filterGroups={filters} />
        <FooterWrapper
          control={control}
          onApply={handleSubmit(onSubmit)}
          onClear={onClear}
        />
      </FormProvider>
    </Sidebar>
  );
}

const FooterWrapper = memo(function FooterWrapper({
  onApply,
  onClear,
  control,
}: {
  onApply: () => void;
  onClear: () => void;
  control: Control<FilterInputData, FilterInputData>;
}) {
  const filterState = useReactiveVar(filterStateVar);
  const formInputValue = useWatch({ control });
  const filterQueriesLoading = useReactiveVar(filterQueriesAreLoading);

  const formState = new FilterState(formInputValue);
  const filterStateUpToDate = filterState?.isEqual(formState);
  const isDirty = formState.activeFilterCount() > 0;
  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    if (!filterQueriesLoading) setFiltersApplied(false);
  }, [filterQueriesLoading]);

  return (
    <Footer
      clearDisabled={!isDirty}
      loading={filterQueriesLoading && filtersApplied}
      onApply={() => {
        setFiltersApplied(true);
        onApply();
      }}
      onClear={() => {
        initialFilterStateVar(new FilterState({}));
        onClear();
      }}
      applyDisabled={filterStateUpToDate}
    />
  );
});
